import { Link, useLocation } from 'react-router-dom';
import { useChannel } from '../contexts/ChannelContext';

const WING_IMG = 'https://customer-assets.emergentagent.com/job_jay-pernell-sports/artifacts/z7u9j3pl_white%20wings_edited.png';

/**
 * Returns the per-channel wordmark JSX (e.g. "FLYT ✈ LEAGUE"). Used inside
 * the navbar AND the standalone strip; the navbar variant suppresses the
 * inline tagline so it can render below on its own row instead.
 *
 * iter476 · Wings image needs its `filter` swapped based on the surrounding
 * background: on a DARK header the LEAGUE wings must render white
 * (brightness(0) invert(1)); on a LIGHT header black. Prior code hard-
 * coded `brightness(0)` (pure black) which turned the wings invisible
 * against the dark web navbar — reading as an off-spaced "FLYT   LEAGUE".
 * iOS mobile happened to render a light-theme navbar so it looked correct
 * there. Passing `isDark` down keeps both surfaces right.
 */
const buildWordmark = ({ isFlyt, isVisionary, showInlineTagline, isDark = false }) => {
  // Wing filter picker — white on dark surfaces, black on light.
  const wingFilterDark  = 'brightness(0) invert(1)';
  const wingFilterLight = 'brightness(0)';
  if (isFlyt) {
    return (
      <>
        <span className="font-display tracking-tight font-black text-white">FLYT</span>
        <img loading="lazy" decoding="async"
          src={WING_IMG}
          alt=""
          aria-hidden="true"
          className="h-3.5 sm:h-4 w-auto wings-flutter mx-1.5"
          style={{ filter: wingFilterDark }}
        />
        <span className="font-display tracking-tight font-medium text-gray-300">STUDIOS</span>
        {showInlineTagline && (
          <span
            className="hidden sm:inline ml-3 pl-3 border-l border-white/15 text-[10px] sm:text-[11px] uppercase tracking-widest text-gray-400 leading-tight max-w-[260px]"
            data-testid="flyt-tagline-wordmark"
          >
            The studio that powers the FLYT League
          </span>
        )}
      </>
    );
  }
  if (isVisionary) {
    return (
      <>
        <span className="font-display tracking-tight font-black text-black">DREAMERS</span>
        <img loading="lazy" decoding="async"
          src={WING_IMG}
          alt=""
          aria-hidden="true"
          className="h-3.5 sm:h-4 w-auto wings-flutter mx-1.5"
          style={{ filter: wingFilterLight }}
        />
        <span
          className="font-display tracking-tight font-black"
          style={{
            background: 'linear-gradient(135deg, #4a9edd, #6db4eb, #8cc8f5, #6db4eb, #2c7bb8)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          NETWORK
        </span>
      </>
    );
  }
  // Dreams League → branded as FLYT
  // iter816 · Was hardcoded text-white — invisible on the one light
  // navbar route this branch now needs to support (/network-profile).
  // The wing icon + tagline below already switched on `isDark`; this
  // just brings the wordmark text itself in line with them.
  return (
    <>
      <span className={`font-display tracking-tight font-black ${isDark ? 'text-white' : 'text-black'}`}>FLYT</span>
      <img loading="lazy" decoding="async"
        src={WING_IMG}
        alt=""
        aria-hidden="true"
        className="h-3.5 sm:h-4 w-auto wings-flutter ml-1.5"
        style={{ filter: isDark ? wingFilterDark : wingFilterLight }}
      />
      {showInlineTagline && (
        <span
          className={`hidden sm:inline ml-3 pl-3 border-l text-[10px] sm:text-[11px] uppercase tracking-widest leading-tight max-w-[260px] ${
            isDark ? 'border-white/15 text-gray-400' : 'border-gray-300 text-gray-500'
          }`}
          data-testid="league-tagline-wordmark"
        >
          Own your athletic career.
        </span>
      )}
    </>
  );
};

/**
 * Compact inline wordmark used in the mobile navbar's top-left slot.
 * Renders the wordmark on row 1 and the tagline below on row 2 — both
 * stacked tightly so the navbar height (h-16) still fits.
 */
export const NavbarWordmark = ({ isDark = false }) => {
  const channel = useChannel();
  const isFlyt = channel.id === 'flyt';
  const isVisionary = channel.id === 'dreams_visionary';

  const tagline = isFlyt
    ? 'The studio that powers the FLYT League'
    : isVisionary
    ? null
    : 'Own your athletic career.';

  return (
    <Link
      to={channel.homePath}
      className="inline-flex flex-col items-start leading-none hover:opacity-90 transition"
      data-testid="navbar-wordmark-link"
    >
      <span className="inline-flex items-center text-[13px] sm:text-sm leading-none">
        {buildWordmark({ isFlyt, isVisionary, showInlineTagline: false, isDark })}
      </span>
      {tagline && (
        <span
          className={`text-[8.5px] uppercase tracking-[0.18em] leading-tight mt-1 ${
            isDark ? 'text-gray-400' : 'text-gray-500'
          }`}
          data-testid="navbar-wordmark-tagline"
        >
          {tagline}
        </span>
      )}
    </Link>
  );
};

/**
 * Standalone wordmark strip directly below the navbar (DESKTOP ONLY now —
 * mobile gets the wordmark embedded in the navbar via `<NavbarWordmark/>`).
 * Static per channel.
 *   • LEAGUE   : "FLYT ✈ LEAGUE" (LEAGUE in blue)
 *   • DREAMERS : "DREAMERS ✈ NETWORK"
 *   • FLYT     : "FLYT ✈ STUDIOS · the studio that powers the FLYT League"
 */
const ChannelWordmark = () => {
  const channel = useChannel();
  const location = useLocation();
  const isFlyt = channel.id === 'flyt';
  const isVisionary = channel.id === 'dreams_visionary';
  const isNetworkRoute = location.pathname.startsWith('/network')
    || location.pathname.startsWith('/visionary/network');
  const isDark = channel.theme === 'dark' || isNetworkRoute;

  const wordmarkContent = buildWordmark({ isFlyt, isVisionary, showInlineTagline: true, isDark });

  const stripClass = isDark
    ? 'w-full bg-black border-b border-white/5'
    : 'w-full bg-white border-b border-gray-100';

  return (
    <div className={`hidden md:block ${stripClass}`} data-testid={`channel-wordmark-${channel.id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-2.5 sm:py-3">
        <div className="flex items-center justify-start">
          <Link
            to={channel.homePath}
            className="inline-flex items-center gap-0 text-sm leading-none hover:opacity-90 transition"
            data-testid="channel-wordmark-link"
          >
            {wordmarkContent}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ChannelWordmark;
